import { useQuery } from "@apollo/client";
import { getOrganizationCoursesUserCountQuery } from "../modules/EventModalV2Query";
import { useMemo, useState, useEffect, useRef } from "react";
import _ from "lodash";

/**
 * Custom React hook for fetching and managing course user count data with optimized caching and debouncing.
 *
 * This hook provides efficient data fetching for course user counts by implementing:
 * - Debounced query execution to prevent excessive API calls
 * - Stable reference management for cache optimization
 * - Optimistic caching strategies with cache-and-network policies
 * - Loading state management with preloading indicators
 *
 * @param {string[]} visibleCoursesIds - Array of course IDs that are currently visible/active
 * @param {boolean} viewOnly - Flag to skip query execution when in view-only mode
 * @param {React.MutableRefObject} courseCountMappingRef - Ref object to store persistent course count mapping across renders
 *
 * @returns {Object} Hook result object
 * @returns {Object} returns.countMapping - Mapping of course IDs to their respective course data with user counts
 * @returns {boolean} returns.loading - Loading state (true when fetching data or during debounce preloading)
 * @returns {Error|undefined} returns.error - GraphQL query error if any occurred during data fetching
 *
 * @example
 * const courseCountMappingRef = useRef({});
 * const { countMapping, loading, error } = useCourseCount(
 *   ['course1', 'course2'],
 *   false,
 *   courseCountMappingRef
 * );
 *
 * @hook
 * @since 1.0.0
 */
const useCourseCount = (visibleCoursesIds, viewOnly, courseCountMappingRef) => {
  // Add debouncing to prevent too frequent query updates
  const [debouncedVisibleCoursesIds, setDebouncedVisibleCoursesIds] = useState(
    visibleCoursesIds
  );

  // Use ref to store previous courseIds for stable references
  const previousCourseIdsRef = useRef([]);
  const [preLoading, setPreLoading] = useState(false);

  useEffect(() => {
    setPreLoading(true);
    const timeoutId = setTimeout(() => {
      setPreLoading(false);
      setDebouncedVisibleCoursesIds(visibleCoursesIds);
    }, 300); // Increased debounce time for better caching

    return () => clearTimeout(timeoutId);
  }, [JSON.stringify(visibleCoursesIds)]);

  // Create stable courseIds array that maintains reference equality when contents are the same
  const courseIds = useMemo(() => {
    if (_.isEmpty(debouncedVisibleCoursesIds)) {
      // Return empty array but maintain reference stability
      const emptyArray = [];
      previousCourseIdsRef.current = emptyArray;
      return emptyArray;
    }

    const sortedIds = _.sortBy(debouncedVisibleCoursesIds);

    // Check if the new array is the same as the previous one
    if (_.isEqual(sortedIds, previousCourseIdsRef.current)) {
      // Return the previous reference to maintain cache key stability
      return previousCourseIdsRef.current;
    }

    // Store new array as previous for next comparison
    previousCourseIdsRef.current = sortedIds;
    return sortedIds;
  }, [debouncedVisibleCoursesIds]);

  const { data, loading, error } = useQuery(
    getOrganizationCoursesUserCountQuery,
    {
      variables: {
        courseIds,
      },
      context: {
        useHttpLink: true,
      },
      skip: _.isEmpty(courseIds) || viewOnly,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first", // Use cache-first for subsequent fetches
      notifyOnNetworkStatusChange: true, // Better loading state management
      errorPolicy: "all", // Return partial data on error
    }
  );
  const coursesCountMapping = useMemo(() => {
    const multiNodeData = _.get(data, "multiNode", []);
    const mappingCourse = multiNodeData.reduce((mapping, course) => {
      mapping[course.id] = course;
      return mapping;
    }, {});
    courseCountMappingRef.current = {
      ...courseCountMappingRef.current,
      ...mappingCourse,
    };
    return mappingCourse;
  }, [data, courseCountMappingRef]);

  // Memoize the return object to prevent unnecessary re-renders
  const result = useMemo(
    () => ({
      countMapping: coursesCountMapping,
      loading: (loading && _.isEmpty(data)) || preLoading, // Only show loading when there's no cached data
      error: error,
    }),
    [coursesCountMapping, loading, error, data, preLoading]
  );

  return result;
};

export default useCourseCount;
